import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const canneseries: ChauffeurPage = {
  slug: "canneseries",
  title: "Chauffeur privé CANNESERIES – VTC festival de séries",
  subtitle:
    "Chauffeur privé pendant CANNESERIES à Cannes : tapis rose, accueil aéroport de Nice et mises à disposition autour du Palais des Festivals.",
  keywords: [
    "chauffeur canneseries",
    "vtc canneseries",
    "chauffeur festival séries cannes",
  ],
  hero: {
    title: "Chauffeur privé pendant CANNESERIES",
    subtitle:
      "Festival international des séries en avril : tapis rose, projections et soirées avec un chauffeur dédié autour du Palais des Festivals.",
    keywords: [
      "chauffeur CANNESERIES",
      "VTC festival de séries",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant CANNESERIES",
    badges: ["Accueil aéroport VIP", "Dépose tapis rose", "Runs de nuit"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant CANNESERIES",
    items: [
      {
        title: "Tapis rose et Palais",
        description:
          "Autour des projections, les abords du Palais des Festivals se densifient. Le chauffeur vous dépose au plus près selon le périmètre et l'horaire du tapis rose.",
      },
      {
        title: "Accueil aéroport de Nice",
        description:
          "En avril, talents, presse et invités arrivent par Nice Côte d'Azur. Accueil pancarte, suivi de vol et transfert direct vers l'hôtel.",
      },
      {
        title: "Talents, presse, industrie",
        description:
          "Flux distincts pour comédiens, équipes presse et professionnels des séries : ponctualité avant projections et panels.",
      },
      {
        title: "Runs de nuit assurés",
        description:
          "Projections en soirée et fêtes du festival : retour tardif avec un chauffeur qui patiente.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant CANNESERIES",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Cannes avec suivi de vol pendant le festival de séries.",
      },
      {
        title: "Dépose tapis rose",
        text: "Approche discrète au plus près du Palais des Festivals selon le périmètre et l'horaire de projection.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner projections, panels et rendez-vous.",
      },
      {
        title: "Soirées & fêtes du festival",
        text: "Hôtels de la Croisette, plages privées et dîners, retour de nuit garanti.",
      },
      {
        title: "Navettes équipes & presse",
        text: "Coordination multi-véhicules pour délégations, attachés de presse et partenaires.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant CANNESERIES",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Tapis rose, salles de projection et espaces de rencontres.",
      },
      {
        name: "La Croisette",
        description: "Hôtels (Carlton, Martinez, Majestic) et plages privées.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, arrivées des talents et de la presse.",
      },
      {
        name: "Cannes centre & gare",
        description: "Accès et hébergements du public et des professionnels.",
      },
      {
        name: "Mougins & arrière-pays",
        description: "Villas, dîners et soirées hors du centre.",
      },
    ],
  },
  generic: {
    heading: "CANNESERIES vu d'un chauffeur",
    subheading:
      "En avril, le Palais des Festivals déroule son tapis rose pour le festival international des séries.",
    paragraphs: [
      "Chaque printemps, CANNESERIES investit le Palais des Festivals de Cannes pour célébrer les séries : projections, compétition et tapis rose au même emplacement que la grande montée des marches. Un chauffeur qui connaît les déposes autorisées vous rapproche au maximum sans manquer votre séance.",
      "Talents, presse et invités arrivent surtout par l'aéroport de Nice Côte d'Azur. Avec le suivi de vol et l'accueil pancarte, nous absorbons les décalages en avril pour vous déposer à l'hôtel sans attente.",
      "Le festival enchaîne projections, panels et soirées. La mise à disposition garde le même chauffeur sur la journée et assure les retours de nuit après les fêtes.",
    ],
    bullets: [
      "Dépose au plus près du Palais",
      "Suivi de vol en avril",
      "Retours après minuit",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour CANNESERIES",
    steps: [
      "Indiquez vos dates du festival, votre hôtel et vos horaires de projection.",
      "Nous calons les itinéraires selon le périmètre du Palais et l'affluence du tapis rose.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir projections, panels et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pour CANNESERIES ?",
      a: "Réservez tôt : en avril, Cannes accueille le festival des séries et la demande grimpe. Précisez vos dates, votre hôtel et vos horaires de projection pour un chauffeur dédié sur toute la durée.",
    },
    {
      q: "Pouvez-vous déposer au plus près du tapis rose ?",
      a: "Oui. Pendant CANNESERIES, les abords du Palais des Festivals sont densifiés. Le chauffeur vous dépose au point autorisé le plus proche selon le périmètre et l'horaire, puis vous reprend à la sortie.",
    },
    {
      q: "Gérez-vous les arrivées à l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. Talents et presse arrivent par Nice Côte d'Azur ; nous adaptons l'heure de prise en charge en cas de retard.",
    },
    {
      q: "Proposez-vous une mise à disposition sur la journée ?",
      a: "Oui. Un chauffeur dédié reste disponible pour enchaîner projections, panels et rendez-vous professionnels sans vous soucier du stationnement à Cannes.",
    },
    {
      q: "Assurez-vous les retours après les soirées ?",
      a: "Oui. Les projections du soir et les fêtes du festival se terminent tard : le chauffeur patiente et garantit votre retour de nuit.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour CANNESERIES ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : chauffeur en tenue, ponctuel et discret, habitué aux événements du Palais des Festivals.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Transferts avec chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Aéroport de Nice, tarifs et trajets",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Location de voiture à Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur pour CANNESERIES",
    text: "Accueil aéroport, tapis rose et soirées : réservez un chauffeur dédié pour le festival des séries.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur CANNESERIES — VTC festival de séries",
    description:
      "Chauffeur privé pendant CANNESERIES à Cannes : dépose tapis rose au Palais des Festivals, accueil aéroport de Nice, mise à disposition et runs de nuit.",
    canonical: "/chauffeur/canneseries",
  },
};

export default canneseries;
